import React from 'react';
import { motion } from 'framer-motion';
import './Legal.css';

const TermsConditions = () => {
  return (
    <motion.div 
      className="legal-content"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="legal-header">
        <a href="/" className="back-link">← Back to Home</a>
        <h1>Terms & Conditions</h1>
        <p className="last-updated">Last updated: April 16, 2026</p>
      </div>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
      >
        <h2>1. Agreement to Terms</h2>
        <p>These Terms and Conditions constitute a legally binding agreement made between you, whether personally or on behalf of an entity (“you”) and Grindon Gym (“we,” “us” or “our”), concerning your access to and use of our mobile application and any other media form, media channel, mobile website or mobile application related, linked, or otherwise connected thereto (collectively, the “App”).</p>
        <p>You agree that by accessing the App, you have read, understood, and agree to be bound by all of these Terms and Conditions. If you do not agree with all of these Terms and Conditions, then you are expressly prohibited from using the App and you must discontinue use immediately.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        <h2>2. Use of Service and Account Responsibilities</h2>
        <p>If you use the App, you are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer or mobile device, and you agree to accept responsibility for all activities that occur under your account or password.</p>
        <p>We reserve the right to refuse service, terminate accounts, remove or edit content, or cancel orders in our sole discretion.</p>
        <p>To use certain features of the App, you must:</p>
        <ul>
          <li>Provide accurate, current and complete information.</li>
          <li>Promptly update such information to keep it accurate, current and complete.</li>
          <li>Maintain the security of your password and identification.</li>
        </ul>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        <h2>3. Intellectual Property Rights</h2>
        <p>Unless otherwise indicated, the App is our proprietary property and all source code, databases, functionality, software, website designs, audio, video, text, photographs, and graphics on the App (collectively, the “Content”) and the trademarks, service marks, and logos contained therein (the “Marks”) are owned or controlled by us or licensed to us, and are protected by copyright and trademark laws.</p>
        <p>Except as expressly provided in these Terms and Conditions, no part of the App and no Content or Marks may be copied, reproduced, aggregated, republished, uploaded, posted, publicly displayed, encoded, translated, transmitted, distributed, sold, licensed, or otherwise exploited for any commercial purpose whatsoever, without our express prior written permission.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
      >
        <h2>4. Fitness and Health Disclaimer</h2>
        <p><strong>Physical exercise can be strenuous and subject to risk of serious injury.</strong> Grindon Gym urges you to obtain a physical examination from a doctor before using any exercise equipment or participating in any exercise activity. You agree that by participating in physical exercise or training activities, you do so entirely at your own risk.</p>
        <p>Any information or guidance provided by our platform is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. Never disregard professional medical advice or delay in seeking it because of something you have read on this App.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
      >
        <h2>5. Prohibited Activities</h2>
        <p>You may not access or use the App for any purpose other than that for which we make the App available. The App may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.</p>
        <p><strong>Zero tolerance:</strong> Grind On does not tolerate objectionable content or abusive users. We prohibit harassment, bullying, hate speech, sexually explicit content, graphic violence, threats, spam, impersonation, and any content or behavior intended to intimidate, humiliate, exploit, or harm another person or group.</p>
        <p>As a user of the App, you agree not to:</p>
        <ul>
          <li>Systematically retrieve data or other content from the App to create or compile, directly or indirectly, a collection, compilation, database, or directory without written permission from us.</li>
          <li>Make any unauthorized use of the App, including collecting usernames and/or email addresses of users by electronic or other means for the purpose of sending unsolicited email.</li>
          <li>Circumvent, disable, or otherwise interfere with security-related features of the App.</li>
          <li>Engage in unauthorized framing of or linking to the App.</li>
          <li>Trick, defraud, or mislead us and other users, especially in any attempt to learn sensitive account information such as user passwords.</li>
          <li>Post objectionable, abusive, unsafe, explicit, threatening, hateful, or misleading content.</li>
        </ul>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.6 }}
      >
        <h2>6. Community Safety and Moderation</h2>
        <p>We use content filtering to prevent objectionable material from being posted. Users can report posts, comments, and replies in the app, and can block abusive users. Blocking removes that user's content from the reporting user's feed immediately and notifies Grind On.</p>
        <p>Grind On reviews objectionable content reports within 24 hours. If content violates our Terms, EULA, or Community Guidelines, we may remove the content and eject or suspend the user who provided the offending content.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.7 }}
      >
        <h2>7. Membership, Billing and Cancellations</h2>
        <p>Fees for memberships are as set forth in the App. We reserve the right to change our pricing at any time. All payments are non-refundable unless otherwise required by law or specified in a particular promotional offer.</p>
        <p><strong>Cancellations:</strong> You may cancel your membership at any time through the App settings or by contacting support. Cancellation will be effective at the end of the current billing cycle.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
      >
        <h2>8. Limitation of Liability</h2>
        <p>In no event will we or our directors, employees, or agents be liable to you or any third party for any direct, indirect, consequential, exemplary, incidental, special, or punitive damages, including lost profit, lost revenue, loss of data, or other damages arising from your use of the app, even if we have been advised of the possibility of such damages.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.9 }}
      >
        <h2>9. Governing Law</h2>
        <p>These Terms and Conditions and your use of the App are governed by and construed in accordance with the laws of Nepal applicable to agreements made and to be entirely performed within Nepal, without regard to its conflict of law principles.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 1.0 }}
      >
        <h2>10. Modifications and Interruptions</h2>
        <p>We reserve the right to change, modify, or remove the contents of the App at any time or for any reason at our sole discretion without notice. We also reserve the right to modify or discontinue all or part of the App without notice at any time. We will not be liable to you or any third party for any modification, price change, suspension, or discontinuance of the App.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 1.1 }}
      >
        <h2>11. Contact Us</h2>
        <p>In order to resolve a complaint regarding the App or to receive further information regarding use of the App, please contact us at:</p>
        <p>
          Grindon Gym<br />
          Sallaghari, Bhaktapur<br />
          Bagmati, 44800<br />
          Nepal<br />
          Email: <strong><a href="mailto:support@grindon.fitness">support@grindon.fitness</a></strong>
        </p>
      </motion.section>
    </motion.div>
  );
};

export default TermsConditions;
